import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FilePdfIcon } from '@phosphor-icons/react';
import { Button, Dialog, EmptyState } from '@origo/origo-ui';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import DocumentList, { type DocumentAction } from './components/DocumentList';
import NewDocumentDialog from './components/NewDocumentDialog';
import type { DocumentFieldErrors } from './components/DocumentForm';
import type { DocumentFormValues, DocumentItem } from '../../types';
import styles from './DocumentsPage.module.css';

type StubKind = DocumentAction | null;

const stubKeyMap: Record<Exclude<StubKind, null>, 'replaceStub' | 'deleteStub'> = {
    replace: 'replaceStub',
    delete: 'deleteStub',
};

export interface NewDocumentState {
    open: boolean;
    values: DocumentFormValues;
    file: File | null;
    fieldErrors: DocumentFieldErrors;
    fileError?: string;
    isSaving: boolean;
}

interface DocumentsPageProps {
    items: DocumentItem[];
    onOpen: (id: string) => void;

    // Nytt dokument (F3.1)
    newDocument: NewDocumentState;
    onNewOpen: () => void;
    onNewClose: () => void;
    onNewChange: (field: keyof DocumentFormValues, value: string) => void;
    onNewFileChange: (file: File | null) => void;
    onNewSave: () => void;
}

const DocumentsPage: React.FC<DocumentsPageProps> = ({
    items,
    onOpen,
    newDocument,
    onNewOpen,
    onNewClose,
    onNewChange,
    onNewFileChange,
    onNewSave,
}) => {
    const { t } = useTranslation();
    const [stub, setStub] = useState<StubKind>(null);

    const isEmpty = items.length === 0;

    const stubKey = stub ? stubKeyMap[stub] : null;
    const stubCopy = stubKey
        ? {
              title: t(`documents.${stubKey}.title`),
              body: t(`documents.${stubKey}.body`),
              close: t(`documents.${stubKey}.close`),
          }
        : null;

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('documents.title')}
                subtitle={t('documents.subtitle')}
                actions={
                    !isEmpty ? (
                        <Button variant="secondary" size="md" onClick={onNewOpen}>
                            {t('documents.newDocument')}
                        </Button>
                    ) : undefined
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<FilePdfIcon size={32} weight="duotone" />}
                        title={t('documents.empty.title')}
                        description={t('documents.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={onNewOpen}>
                                {t('documents.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <DocumentList
                        items={items}
                        onOpen={onOpen}
                        onAction={action => setStub(action)}
                    />
                )}
            </div>
            <NewDocumentDialog
                open={newDocument.open}
                values={newDocument.values}
                file={newDocument.file}
                fieldErrors={newDocument.fieldErrors}
                fileError={newDocument.fileError}
                onChange={onNewChange}
                onFileChange={onNewFileChange}
                onClose={onNewClose}
                onSave={onNewSave}
                isSaving={newDocument.isSaving}
            />
            <Dialog
                open={stub !== null}
                onClose={() => setStub(null)}
                title={stubCopy?.title ?? ''}
                footer={
                    <Button variant="primary" onClick={() => setStub(null)}>
                        {stubCopy?.close ?? ''}
                    </Button>
                }
            >
                <p>{stubCopy?.body ?? ''}</p>
            </Dialog>
        </article>
    );
};

export default DocumentsPage;
